// TCPN-CN1 — Country Filter Normalization.
//
// Frontend may display human-readable names ("Turkey", "Türkiye", "Germany"),
// but every OUTBOUND search query param and every internal `filters.country`
// value MUST be the canonical ISO-3166 alpha-2 code (e.g. "TR", "DE", "ES").
//
// This module is the single SOURCE OF TRUTH for that conversion. It handles:
//   • already-canonical codes ("TR", "tr", "Tr")           → "TR"
//   • catalog names ("Turkey", "TURKEY", "turkey")          → "TR" (from COUNTRIES)
//   • a small set of natural-language aliases ("Türkiye"…)  → "TR"
//
// It NEVER mutates state directly; callers decide where to apply it (URL
// hydration, outbound query build, etc.).
//
// Scope is strictly the country FILTER pipeline. No ranking, trust-score,
// schema, or consent logic touches this file.

import { COUNTRIES } from '../context/ClinicApplicationContext';

// Build a static lookup map: every alias (lowercased, trimmed) → ISO-2 code.
// Aliases include: the ISO code itself, the catalog display name, and a
// curated list of natural-language variants for the markets we serve today.
const _ALIAS_TO_ISO2 = (() => {
  const map = new Map();

  // Code + catalog name from the canonical COUNTRIES list.
  (COUNTRIES || []).forEach(({ code, name }) => {
    if (!code) return;
    const iso = String(code).trim().toUpperCase();
    map.set(iso.toLowerCase(), iso);
    if (name) map.set(String(name).trim().toLowerCase(), iso);
  });

  // Natural-language aliases for markets that actively appear in search.
  // Keep this list short and audit-friendly; expand only when a real owner
  // test surfaces a new alias.
  const aliases = {
    'türkiye': 'TR',
    'turkiye': 'TR',
    'turkey': 'TR',
    'deutschland': 'DE',
    'germany': 'DE',
    'españa': 'ES',
    'espana': 'ES',
    'spain': 'ES',
  };
  Object.entries(aliases).forEach(([k, v]) => map.set(k, v));

  return map;
})();

/**
 * Normalize ANY country-shaped value to its canonical ISO-2 code.
 *
 *   normalizeCountryToISO2("Turkey")   → "TR"
 *   normalizeCountryToISO2("TURKEY")   → "TR"
 *   normalizeCountryToISO2("turkey")   → "TR"
 *   normalizeCountryToISO2("Türkiye")  → "TR"
 *   normalizeCountryToISO2("tr")       → "TR"
 *   normalizeCountryToISO2("TR")       → "TR"
 *   normalizeCountryToISO2("Spain")    → "ES"
 *   normalizeCountryToISO2("")         → ""
 *   normalizeCountryToISO2(null)       → ""
 *   normalizeCountryToISO2("Atlantis") → ""   (unknown → drop, never echo)
 *
 * Important: we DROP unknown values rather than echoing them, because the
 * backend search index keys on ISO-2 only. Echoing "ATLANTIS" would put
 * the UI back in the broken "filter looks active but returns 0" state.
 */
export function normalizeCountryToISO2(value) {
  if (value == null) return '';
  const key = String(value).trim().toLowerCase();
  if (!key) return '';
  return _ALIAS_TO_ISO2.get(key) || '';
}

// Optional: expose the ISO catalog for callers that need labels (avoids
// re-importing COUNTRIES on the public boundary).
export const ISO2_VALID_CODES = new Set(
  (COUNTRIES || []).map(c => String(c.code || '').trim().toUpperCase()).filter(Boolean)
);

/**
 * Reverse lookup: ISO-2 code → catalog display name.
 *
 *   iso2ToCountryName("TR")     → "Turkey"
 *   iso2ToCountryName("tr")     → "Turkey"
 *   iso2ToCountryName("ES")     → "Spain"
 *   iso2ToCountryName("XX")     → ""   (unknown code → empty; callers
 *                                       should fall back to the raw value)
 *
 * Added under TREATX-UAT-RELEASE-FIX-SEQUENCE-01 / Phase 5 so that
 * surfaces displaying a stored country value get a consistent
 * human-readable label regardless of whether the database row holds
 * an ISO-2 code or a legacy display name.
 */
const _ISO2_TO_NAME = (COUNTRIES || []).reduce((acc, c) => {
  if (c?.code) acc[String(c.code).toUpperCase()] = c.name || '';
  return acc;
}, {});
export function iso2ToCountryName(code) {
  if (!code) return '';
  return _ISO2_TO_NAME[String(code).trim().toUpperCase()] || '';
}

/**
 * Display helper: normalise an arbitrary stored value (ISO-2 OR display
 * name OR alias) to its canonical display name. Falls back to the raw
 * value when the input is unknown so we never silently swallow data.
 *
 *   displayCountry("TR")        → "Turkey"
 *   displayCountry("Turkey")    → "Turkey"
 *   displayCountry("Türkiye")   → "Turkey"
 *   displayCountry("Atlantis")  → "Atlantis"   (echoed, not dropped)
 *   displayCountry("")          → ""
 */
export function displayCountry(value) {
  if (!value) return '';
  const iso = normalizeCountryToISO2(value);
  if (iso) return _ISO2_TO_NAME[iso] || iso;
  return String(value).trim();
}

